/**
 * Converts an array of items into a key-value map.
 * @template T
 * @param {T[]} data - The array of items.
 * @param {string} keyField - The field to use as the key.
 * @param {string} valueField - The field to use as the value.
 * @param {string?} secondaryValueField - The field to use as the secondary value.
 * @returns {Record<string, string>} The key-value map.
 */
export function arrayToKeyValueMap(data, keyField, valueField, secondaryValueField = null) {
  return data.reduce((/** @type any */ acc, /** @type any */ item) => {
    acc[item[keyField]] = item[valueField];
    if (secondaryValueField) {
      acc[item[keyField]] += ` (${item[secondaryValueField]})`;
    }
    return acc;
  }, {});
}

/**
 * Converts an array of items into a key-value map.
 * @template T
 * @param {T[]} data - The array of items.
 * @param {string?} filter - The filter to use on the values.
 * @param {string} keyField - The field to use as the key.
 * @param {string} valueField - The field to use as the value.
 * @param {string?} secondaryValueField - The field to use as the secondary value.
 * @returns {Record<string, string>} The key-value map.
 */
export function arrayToKeyValueMapFiltering(
  data,
  filter,
  keyField,
  valueField,
  secondaryValueField = null
) {
  return data.reduce((/** @type any */ acc, /** @type any */ item) => {
    if (filter == null || item[valueField].toLowerCase().includes(filter.toLowerCase())) {
      acc[item[keyField]] = item[valueField];
      if (secondaryValueField) {
        acc[item[keyField]] += ` (${item[secondaryValueField]})`;
      }
    }

    return acc;
  }, {});
}

/**
 * Converts an array of numbers into a key-value map, where the key is the number
 * and the value is obtained by calling the provided humanize function.
 *
 * @param {number[]} numbers - The array of numbers.
 * @param {string?} filter - The filter to use on the values.
 * @param {(num: number) => string} humanizeFn - The function that converts a number into a human-readable string.
 * @returns {Record<number, string>} The key-value map where the key is the number and the value is the humanized string.
 */
export function arrayToHumanizedMapFiltering(numbers, filter, humanizeFn) {
  return numbers.reduce((/** @type any */ acc, num) => {
    if (filter == null || humanizeFn(num).toLowerCase().includes(filter.toLowerCase())) {
      acc[num] = humanizeFn(num);
    }
    return acc;
  }, {});
}
